import { CaretDown } from '@phosphor-icons/react';
import { Dropdown } from 'primereact/dropdown';
import type { DropdownContext, DropdownPassThroughOptions } from 'primereact/dropdown';
import { useEffect, useRef } from 'react';

/**
 * Shared passthrough for every dropdown the app restyles.
 *
 * These keep PrimeReact's own classes rather than going `unstyled`, for one reason: in unstyled
 * mode PrimeReact stops positioning the overlay entirely — it drops the `p-dropdown-panel` class
 * the theme aligns against, appends the panel to `body` and sets no inline `top`/`left`, so the
 * option list lands at the bottom of the document. Keeping the classes keeps `alignOverlay`
 * working, and the look is taken back from the bootstrap theme and from PrimeReact's app-wide
 * Tailwind passthrough with Tailwind's trailing `!`, which is what it takes to beat a theme rule
 * of three chained classes.
 */
export const DROPDOWN_OVERLAY_PT = {
  panel: { className: 'mt-1 rounded-lg! border! border-gray-200! bg-white! py-1! shadow-lg!' },
  wrapper: { className: 'max-h-56! overflow-auto rounded-none! border-0! bg-transparent! shadow-none!' },
  list: { className: 'm-0! list-none! p-0!' },
  item: ({ context }: { context: DropdownContext }) => ({
    className: `m-0! cursor-pointer rounded-none! px-3! py-2! text-sm! ${
      context.selected
        ? 'bg-gray-100! font-medium! text-gray-900!'
        : context.focused
          ? 'bg-gray-50! text-gray-900!'
          : 'bg-transparent! text-gray-700! hover:bg-gray-100! hover:text-gray-900!'
    }`,
  }),
};

/** Root and label shared by every dropdown: a 32 px control with a hairline border, no blue. */
export const DROPDOWN_CONTROL = [
  'flex h-8! items-center rounded-lg! border! border-gray-300! bg-white! px-2! shadow-none!',
  'hover:border-gray-400! [&.p-focus]:border-gray-400! [&.p-focus]:shadow-none!',
].join(' ');

/** The in-cell editor's own trigger: full cell width, so the box does not jump on open. */
const INLINE_DROPDOWN_PT: DropdownPassThroughOptions = {
  root: { className: `${DROPDOWN_CONTROL} w-full! min-w-0!` },
  input: { className: 'flex-1! truncate! p-0! text-left! text-sm! text-gray-700! shadow-none!' },
  trigger: { className: 'ml-1! flex w-auto! shrink-0 items-center text-gray-400!' },
  ...DROPDOWN_OVERLAY_PT,
};

interface ChoiceCellEditorProps<T extends string> {
  /** The cell's current value, or `null` when it has none. */
  value: T | null;
  options: { label: string; value: T }[];
  /** A pick. Fires before `onClose`. */
  onChange: (value: T | null) => void;
  /** The panel closed — by a pick, by Escape or by a click outside. */
  onClose: () => void;
  placeholder?: string;
  ariaLabel?: string;
}

/**
 * The dropdown a classification cell turns into while it is being edited.
 *
 * It opens itself from an effect rather than through `showOnFocus` + `autoFocus`, and that is
 * load-bearing: a Dropdown whose panel is already open on its very first render never runs the
 * enter phase of the transition — which is exactly where PrimeReact measures the cell and places
 * the overlay — so the option list ends up unpositioned at the bottom of the document instead of
 * under the cell. Opening it one tick later puts the open on a later render, and the alignment
 * runs.
 */
export const ChoiceCellEditor = <T extends string>({
  value,
  options,
  onChange,
  onClose,
  placeholder,
  ariaLabel,
}: ChoiceCellEditorProps<T>) => {
  const dropdownRef = useRef<Dropdown>(null);

  useEffect(() => {
    dropdownRef.current?.focus();
    const timer = window.setTimeout(() => dropdownRef.current?.show(), 0);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <Dropdown
      ref={dropdownRef}
      value={value}
      options={options}
      onChange={(e) => onChange((e.value ?? null) as T | null)}
      onHide={onClose}
      placeholder={placeholder}
      pt={INLINE_DROPDOWN_PT}
      dropdownIcon={<CaretDown size={14} aria-hidden="true" />}
      aria-label={ariaLabel}
    />
  );
};
